import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ApiException } from "@/api/client";
import { useSession } from "@/session/SessionProvider";

export function LoginPage() {
	const { isAuthenticated, isLoading, loginPassword, loginToken } =
		useSession();
	const [params] = useSearchParams();
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [token, setToken] = useState(params.get("token") ?? "");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	// Auto-exchange ?token=
	useEffect(() => {
		const t = params.get("token");
		if (!t || isAuthenticated || isLoading) return;
		let cancelled = false;
		(async () => {
			setBusy(true);
			try {
				await loginToken(t);
				if (!cancelled) navigate("/links", { replace: true });
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof ApiException
							? err.message
							: "Token login failed",
					);
					setBusy(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [params, isAuthenticated, isLoading, loginToken, navigate]);

	if (isLoading) {
		return (
			<div className="grid min-h-svh place-items-center text-fg-muted">
				Loading…
			</div>
		);
	}
	if (isAuthenticated) return <Navigate to="/links" replace />;

	async function onPassword(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setBusy(true);
		try {
			await loginPassword(email, password);
			navigate("/links", { replace: true });
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Login failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onToken(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setBusy(true);
		try {
			await loginToken(token.trim());
			navigate("/links", { replace: true });
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Token login failed",
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="grid min-h-svh place-items-center px-4">
			<div className="w-full max-w-md space-y-8">
				<div className="text-center">
					<h1 className="text-2xl font-semibold tracking-tight text-accent">
						Shorty
					</h1>
					<p className="mt-1 text-sm text-fg-muted">abe.lt admin</p>
				</div>

				{error && (
					<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
						{error}
					</div>
				)}

				<form onSubmit={onPassword} className="space-y-3 rounded-xl border border-border bg-surface-raised p-5">
					<h2 className="text-sm font-medium">Password</h2>
					<label className="block text-sm">
						<span className="text-fg-muted">Email</span>
						<input
							type="email"
							autoComplete="username"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-fg outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
					<label className="block text-sm">
						<span className="text-fg-muted">Password</span>
						<input
							type="password"
							autoComplete="current-password"
							required
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-fg outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
					<button
						type="submit"
						disabled={busy}
						className="w-full rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
					>
						Log in
					</button>
				</form>

				<form onSubmit={onToken} className="space-y-3 rounded-xl border border-border bg-surface-raised p-5">
					<h2 className="text-sm font-medium">Access token</h2>
					<label className="block text-sm">
						<span className="text-fg-muted">Token</span>
						<input
							type="text"
							value={token}
							onChange={(e) => setToken(e.target.value)}
							placeholder="Paste access token"
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-fg outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
					<button
						type="submit"
						disabled={busy || !token.trim()}
						className="w-full rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-surface disabled:opacity-50"
					>
						Use login link
					</button>
				</form>

				<p className="text-center text-sm text-fg-muted">
					<Link to="/reset" className="text-accent hover:underline">
						Forgot password?
					</Link>
				</p>
			</div>
		</div>
	);
}
